/* eslint-disable react/display-name */
'use client';
import React from 'react';

import cx from 'classnames';
import { isEmpty } from 'lodash';

import { InputProps } from './types';
import { SIZE_VS_HEIGHT } from './constant';
import Icon from '../icons';
import {
  numberWithCommas,
  numberWithoutCommas,
  validateEmail,
  validateNumber,
  validatePassword,
} from './helpers';
import FieldLabel from '../FieldLabel';
import SupportTextSection from './SupportTextSection';

import styles from './input.module.scss';

//color prop for input borders
//loading

//theme
//autofill take care
//comments so developer can get suggetion -- documentation

const Input = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    variant = 'outlined',
    size = 'medium',
    type = 'string',
    width,
    className,
    inputClassName,
    containerClassName,
    placeholder,
    label,
    disabled,
    isRequiredField,
    prefixIcon,
    suffixIcon,
    labelPrefixIcon,
    labelSuffixIcon,
    labelClassName,
    onChange,
    value,
    onPrefixIconClick,
    onSuffixIconClick,
    direction,
    maxLength = 56,
    setErrors,
    errors,
    id = type || 'input',
    validation,
    showCharCount,
    prefixIconClassName,
    suffixIconClassName,
    labelPrefixIconClassName,
    labelSuffixIconClassName,
    onEnterClick,
    onBlur,
    ...rest
  } = props;

  //! We'll implement this later
  const directionToUser = direction;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (setErrors) setErrors({ ...errors, [id]: '' });
    if (!onChange) return;

    const inputValue = e.target.value;

    if (inputValue.length > maxLength) return;

    if (type === 'number') {
      if (validateNumber(inputValue)) {
        if (isEmpty(inputValue)) return onChange(inputValue);
        const numebr = parseInt(inputValue);
        onChange(numebr.toString());
      }
    } else if (type === 'number-with-comma') {
      onChange(numberWithoutCommas(inputValue));
    } else {
      onChange(inputValue);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (type === 'email') {
      const err = validateEmail(e.target.value);
      if (setErrors) setErrors({ ...errors, [id]: err });
    }
    if (type === 'password') {
      const err = validatePassword(e.target.value);
      if (setErrors) setErrors({ ...errors, [id]: err });
    }

    if (onBlur) {
      onBlur(e);
    }
  };

  const inputStatus = errors?.[id] ? 'error' : validation?.status;

  return (
    <div
      className={cx(styles.inputField, containerClassName, { [styles.disabled]: disabled })}
      dir={directionToUser}
      style={{
        width,
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'no-drop' : 'auto',
        // pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      <FieldLabel
        labelText={label}
        prefixIcon={labelPrefixIcon}
        suffixIcon={labelSuffixIcon}
        labelClassName={labelClassName}
        labelFor={id}
        isRequiredField={isRequiredField}
        prefixIconClassName={labelPrefixIconClassName}
        suffixIconClassName={labelSuffixIconClassName}
        direction={directionToUser}
      />
      <div
        className={cx(
          styles.input,
          { [styles.outlinedInput]: variant === 'outlined' },
          {
            [styles.defaultOutline]: inputStatus === 'default' && variant === 'outlined',
            [styles.errorOutline]: inputStatus === 'error' && variant === 'outlined',
            [styles.successOutline]: inputStatus === 'success' && variant === 'outlined',
            [styles.warningOutline]: inputStatus === 'warning' && variant === 'outlined',
          },
          { [styles.filledInput]: variant === 'filled' },
          className,
        )}
        style={{
          // height: SIZE_VS_HEIGHT[size],
          // opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'no-drop' : 'auto',
          pointerEvents: disabled ? 'none' : 'auto',
        }}
      >
        <Icon
          icon={prefixIcon}
          onClick={onPrefixIconClick}
          style={{
            marginLeft: 14,
            marginRight: 8,
          }}
          className={prefixIconClassName}
        />
        <input
          type={type}
          placeholder={placeholder}
          className={cx(styles.mainInput, inputClassName)}
          disabled={disabled}
          onChange={handleChange}
          onBlur={handleBlur}
          value={type === 'number-with-comma' ? numberWithCommas(value || '0') : value}
          ref={ref}
          style={{
            textAlign: directionToUser === 'rtl' ? 'right' : 'left',
            paddingLeft: isEmpty(prefixIcon) ? 14 : 0,
            paddingRight: isEmpty(suffixIcon) ? 14 : 0,
            // opacity: disabled ? 0.5 : 1,
            cursor: disabled ? 'no-drop' : 'auto',
            pointerEvents: disabled ? 'none' : 'auto',
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              if (onEnterClick) onEnterClick();
            }
          }}
          {...rest}
        />
        <Icon
          icon={suffixIcon}
          onClick={onSuffixIconClick}
          style={{
            marginRight: 14,
            marginLeft: 8,
          }}
          className={suffixIconClassName}
        />
      </div>
      <SupportTextSection
        text={errors?.[id] || validation?.text}
        className={validation?.className}
        status={errors?.[id] ? 'error' : validation?.status}
        maxLength={maxLength}
        showCharCount={showCharCount}
      />
    </div>
  );
});

export default Input;
